import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IAPIState, IInfoBlockItemType } from "shared/types";
import { CreateInfoBlockItemModal as Component } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import {
  CreateInfoBlockItemBaseProps,
  useCreateInfoBlockItem,
} from "shared/utils";
import { useBandContext } from "Bands/contexts";
import { getInfoBlockItemKey, useClearActionMetaOnUnMount } from "Bands/utils";
import { BAND_ACTIONS } from "Bands/types";
import {
  clearActionMeta,
  clearActionPagination,
  createInfoBlockItem,
  dataMetaSelector,
} from "Bands/state";

export const CreateInfoBlockItemModal: React.FC<CreateInfoBlockItemBaseProps> =
  ({
    type,
    patchInfoBlock,
    hasError,
    handleClose,
    infoBlockApiStatus,
    ...rest
  }) => {
    const { bandId } = useBandContext();
    const dispatch = useDispatch();
    const paginationKey = useMemo(
      () => (bandId ? getInfoBlockItemKey(bandId, type) : ""),
      [bandId, type]
    );

    useClearActionMetaOnUnMount(BAND_ACTIONS.createInfoBlockItem);

    const { api: apiMeta, createdItem } = useSelector(
      dataMetaSelector(BAND_ACTIONS.createInfoBlockItem)
    );

    useEffect(() => {
      if (apiMeta?.status === IAPIState.succeeded) {
        dispatch(clearActionMeta(BAND_ACTIONS.createInfoBlockItem));

        // delete paginated info block items from store,
        // items have to be fetched again after a new item is added
        dispatch(clearActionPagination(paginationKey));

        // Patch/post infoBlock with new contact
        createdItem && patchInfoBlock && patchInfoBlock(createdItem, type);
      }
    }, [
      apiMeta?.status,
      dispatch,
      createdItem,
      patchInfoBlock,
      type,
      paginationKey,
    ]);

    useEffect(() => {
      // Clean up error message on modal unmount
      return () => {
        dispatch(clearActionMeta(BAND_ACTIONS.createInfoBlockItem));
      };
    }, [dispatch]);

    const handleSubmit = async (item: Partial<IInfoBlockItemType>) => {
      const cleanItem = {
        ...item,
        band: bandId ? API_ENDPOINTS.BAND(bandId) : undefined,
      };

      if (bandId) {
        dispatch(createInfoBlockItem({ item: cleanItem, type }));
      }
    };

    const { itemReadbleName, formProps, Form } = useCreateInfoBlockItem({
      type,
      handleClose,
      handleSubmit,
      infoBlockApiStatus,
      hasError: !!apiMeta?.error || hasError,
      createApiStatus: apiMeta?.status,
    });

    return (
      <Component
        type={type}
        handleClose={handleClose}
        itemReadbleName={itemReadbleName}
        Component={Form()}
        componentProps={formProps}
        {...rest}
      />
    );
  };
